// Calls to the Voxie API (/v1, same origin: nginx in the web image or the Vite proxy forwards it).

export class ApiError extends Error {
  readonly status: number;
  /** The server's ErrorResponse.code (e.g. INVALID_CODE), or "NETWORK" when it was not reached. */
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export interface ApiInit extends Omit<RequestInit, "body"> {
  json?: unknown;
  body?: BodyInit;
  token?: string;
}

/** GET or send JSON to [path]; the answer parsed as T (one of the types in contract.gen.ts). */
export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.json !== undefined) headers.set("content-type", "application/json");
  if (init.token) headers.set("authorization", `Bearer ${init.token}`);
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers,
      body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
      credentials: "same-origin",
    });
  } catch (e) {
    throw new ApiError(0, "NETWORK", `${init.method ?? "GET"} ${path}: ${String(e)}`);
  }
  if (!response.ok) {
    const text = await response.text();
    let code = `HTTP_${response.status}`;
    try {
      code = (JSON.parse(text) as { code?: string }).code ?? code;
    } catch {
      // Not JSON (a proxy's error page): keep the status.
    }
    throw new ApiError(response.status, code, `${init.method ?? "GET"} ${path}: ${response.status} ${text}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
